'use client';

import React, { useState, useEffect } from 'react';
import { Plus, X, Upload, Crown, Loader2, Star } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

interface Finalist {
  id: string;
  name: string;
  age: number;
  province: string;
  advocacy: string;
  imageUrl: string;
  imageAlt: string;
  institution: string;
}

const SA_PROVINCES = [
  'Gauteng', 'Western Cape', 'Eastern Cape', 'KwaZulu-Natal',
  'Limpopo', 'Mpumalanga', 'Free State', 'North West', 'Northern Cape',
];

interface AddFinalistForm {
  name: string;
  age: string;
  province: string;
  advocacy: string;
  institution: string;
}

export default function FinalistsContent() {
  const [finalists, setFinalists] = useState<Finalist[]>([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<AddFinalistForm & { image: string }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<AddFinalistForm>({
    name: '',
    age: '',
    province: '',
    advocacy: '',
    institution: '',
  });

  const supabase = createClient();

  // Load finalists from Supabase
  useEffect(() => {
    const fetchFinalists = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const { data, error } = await supabase
          .from('finalists')
          .select('*')
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Failed to load finalists:', error.message);
          setLoadError('Failed to load finalists.');
        } else {
          const mapped: Finalist[] = (data || []).map((row) => ({
            id: row.id,
            name: row.name,
            age: row.age,
            province: row.province,
            advocacy: row.advocacy,
            institution: row.institution,
            imageUrl: row.image_url,
            imageAlt: row.image_alt,
          }));
          setFinalists(mapped);
        }
      } catch (err) {
        console.error('Unexpected error loading finalists:', err);
        setLoadError('Unexpected error loading finalists.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFinalists();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const validateForm = () => {
    const errs: Partial<AddFinalistForm & { image: string }> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.age || isNaN(Number(form.age))) errs.age = 'Valid age is required';
    if (!form.province) errs.province = 'Province is required';
    if (!form.advocacy.trim()) errs.advocacy = 'Advocacy is required';
    if (!form.institution.trim()) errs.institution = 'Institution is required';
    if (!selectedFile) errs.image = 'Model image is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddFinalist = async () => {
    if (!validateForm()) return;
    setIsAdding(true);

    try {
      const imageUrl = previewUrl || '/assets/images/12eef6ff-c410-4438-b75b-48bf1bf380ed-1784970622837.png';
      const imageAlt = `${form.name} — Miss Universities of Gauteng 2026 finalist`;

      const { data, error } = await supabase
        .from('finalists')
        .insert({
          name: form.name,
          age: Number(form.age),
          province: form.province,
          advocacy: form.advocacy,
          institution: form.institution,
          image_url: imageUrl,
          image_alt: imageAlt,
        })
        .select()
        .single();

      if (error) {
        console.error('Add finalist error:', error.message);
        setIsAdding(false);
        return;
      }

      const newFinalist: Finalist = {
        id: data.id,
        name: data.name,
        age: data.age,
        province: data.province,
        advocacy: data.advocacy,
        institution: data.institution,
        imageUrl: data.image_url,
        imageAlt: data.image_alt,
      };

      setFinalists((prev) => [...prev, newFinalist]);
    } catch (err) {
      console.error('Unexpected error adding finalist:', err);
    }

    setForm({ name: '', age: '', province: '', advocacy: '', institution: '' });
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormErrors({});
    setIsAdding(false);
    setShowAdminPanel(false);
  };

  const handleRemoveFinalist = async (id: string) => {
    try {
      const { error } = await supabase.from('finalists').delete().eq('id', id);
      if (error) {
        console.error('Remove finalist error:', error.message);
        return;
      }
    } catch (err) {
      console.error('Unexpected error removing finalist:', err);
    }
    setFinalists((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div>
      {/* Page Hero */}
      <section style={{ background: '#800000' }} className="py-14 px-4 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.07) 0%, transparent 60%)' }} />
        <div className="max-w-screen-xl mx-auto text-center relative">
          <div className="text-4xl mb-4 crown-icon" role="img" aria-label="Crown">👑</div>
          <h1 className="font-800 text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ color: '#D4AF37' }}>
            2026 FINALISTS
          </h1>
          <div className="section-divider mb-4" />
          <p className="text-white/80 text-sm sm:text-base max-w-xl mx-auto">
            Meet the remarkable young women representing universities across Gauteng
          </p>
        </div>
      </section>

      {/* Admin Bar */}
      <div className="px-4 py-4" style={{ background: '#faf3d0', borderBottom: '1px solid #D4AF37' }}>
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <p className="text-xs font-600" style={{ color: '#800000' }}>
            Admin Panel — {finalists.length} finalist{finalists.length !== 1 ? 's' : ''} added
          </p>
          <button
            onClick={() => setShowAdminPanel(true)}
            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-700 btn-gold shadow-gold"
          >
            <Plus size={16} />
            Add Finalist
          </button>
        </div>
      </div>

      {/* Finalists Grid */}
      <section className="py-14 px-4" style={{ background: '#faf9f7' }}>
        <div className="max-w-screen-xl mx-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <Loader2 size={40} className="animate-spin mb-4" style={{ color: '#D4AF37' }} />
              <p className="text-sm font-500" style={{ color: '#6b6b6b' }}>Loading finalists...</p>
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="text-sm font-500" style={{ color: '#c0392b' }}>{loadError}</p>
            </div>
          ) : finalists.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center mb-6 shadow-gold"
                style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #f0d060 100%)' }}
              >
                <Crown size={40} color="#800000" />
              </div>
              <h2 className="font-700 text-xl mb-2" style={{ color: '#800000' }}>
                No Finalists Yet
              </h2>
              <p className="text-sm max-w-sm leading-relaxed mb-6" style={{ color: '#6b6b6b' }}>
                Finalists for Miss Universities of Gauteng 2026 will be announced here once selections are
                complete. Check back soon!
              </p>
              <button
                onClick={() => setShowAdminPanel(true)}
                className="px-6 py-3 rounded-full text-sm font-700 btn-gold shadow-gold"
              >
                Add First Finalist
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-6">
              {finalists.map((finalist, index) => (
                <div
                  key={finalist.id}
                  className="finalist-card rounded-2xl overflow-hidden card-hover group"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {/* Image */}
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <AppImage
                      src={finalist.imageUrl}
                      alt={finalist.imageAlt}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    />
                    {/* Gradient overlay */}
                    <div
                      className="absolute inset-0"
                      style={{ background: 'linear-gradient(to top, rgba(128,0,0,0.85) 0%, transparent 50%)' }}
                    />
                    {/* Remove button (admin) */}
                    <button
                      onClick={() => handleRemoveFinalist(finalist.id)}
                      className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      style={{ background: 'rgba(128,0,0,0.85)' }}
                      aria-label={`Remove ${finalist.name} from finalists`}
                    >
                      <X size={14} color="#ffffff" />
                    </button>
                    {/* Number badge */}
                    <div
                      className="absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center text-xs font-700"
                      style={{ background: '#D4AF37', color: '#800000' }}
                    >
                      {index + 1}
                    </div>
                    {/* Bottom info overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-white font-700 text-base leading-tight">{finalist.name}</p>
                      <p className="text-xs font-500 mt-0.5" style={{ color: '#D4AF37' }}>
                        Age {finalist.age} · {finalist.province}
                      </p>
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <Star size={14} color="#D4AF37" className="flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-600 uppercase tracking-wide mb-0.5" style={{ color: '#800000' }}>
                          Advocacy
                        </p>
                        <p className="text-sm font-400 leading-snug" style={{ color: '#3a3a3a' }}>
                          {finalist.advocacy}
                        </p>
                      </div>
                    </div>
                    <div
                      className="pt-3 border-t"
                      style={{ borderColor: '#e2ddd6' }}
                    >
                      <p className="text-xs" style={{ color: '#9a9a9a' }}>
                        {finalist.institution}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add Finalist Modal */}
      {showAdminPanel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 modal-backdrop"
          onClick={() => setShowAdminPanel(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-finalist-title"
        >
          <div
            className="bg-white rounded-2xl shadow-card-hover w-full max-w-lg max-h-[90vh] overflow-y-auto scale-in"
            style={{ border: '2px solid #D4AF37' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
              style={{ background: '#800000', borderRadius: '14px 14px 0 0' }}
            >
              <h2 id="add-finalist-title" className="font-700 text-base" style={{ color: '#D4AF37' }}>
                Add Finalist
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Close panel"
              >
                <X size={18} color="#ffffff" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Image Upload */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Model Image <span style={{ color: '#800000' }}>*</span>
                </label>
                <div
                  className={`upload-zone rounded-xl overflow-hidden ${previewUrl ? 'has-file p-0' : 'p-6'}`}
                  onClick={() => document.getElementById('admin-finalist-img')?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && document.getElementById('admin-finalist-img')?.click()}
                  aria-label="Upload model image"
                >
                  <input
                    id="admin-finalist-img"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {previewUrl ? (
                    <div className="relative w-full aspect-[3/4]">
                      <AppImage
                        src={previewUrl}
                        alt="Preview of uploaded finalist model photo"
                        fill
                        className="object-cover"
                        unoptimized
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity" style={{ background: 'rgba(0,0,0,0.4)' }}>
                        <p className="text-white text-sm font-600">Change Image</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={28} color="#D4AF37" />
                      <p className="text-sm font-500" style={{ color: '#800000' }}>
                        Upload model photo
                      </p>
                      <p className="text-xs" style={{ color: '#9a9a9a' }}>JPG or PNG</p>
                    </div>
                  )}
                </div>
                {formErrors.image && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.image}</p>
                )}
              </div>

              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Name &amp; Surname <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Lerato Mokoena"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: formErrors.name ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.name && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Age */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-600" style={{ color: '#000000' }}>
                    Age <span style={{ color: '#800000' }}>*</span>
                  </label>
                  <input
                    type="number"
                    value={form.age}
                    onChange={(e) => setForm((p) => ({ ...p, age: e.target.value }))}
                    placeholder="e.g. 22"
                    className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                    style={{ borderColor: formErrors.age ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                  />
                  {formErrors.age && (
                    <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.age}</p>
                  )}
                </div>

                {/* Province */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-600" style={{ color: '#000000' }}>
                    Province <span style={{ color: '#800000' }}>*</span>
                  </label>
                  <select
                    value={form.province}
                    onChange={(e) => setForm((p) => ({ ...p, province: e.target.value }))}
                    className="input-focus w-full px-4 py-3 rounded-lg text-sm border appearance-none"
                    style={{ borderColor: formErrors.province ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                  >
                    <option value="">Select...</option>
                    {SA_PROVINCES.map((prov) => (
                      <option key={`modal-prov-${prov}`} value={prov}>{prov}</option>
                    ))}
                  </select>
                  {formErrors.province && (
                    <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.province}</p>
                  )}
                </div>
              </div>

              {/* Institution */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Institution <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.institution}
                  onChange={(e) => setForm((p) => ({ ...p, institution: e.target.value }))}
                  placeholder="e.g. University of Pretoria"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: formErrors.institution ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.institution && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.institution}</p>
                )}
              </div>

              {/* Advocacy */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-600" style={{ color: '#000000' }}>
                  Advocacy <span style={{ color: '#800000' }}>*</span>
                </label>
                <p className="text-xs" style={{ color: '#6b6b6b' }}>
                  The cause or issue this finalist champions
                </p>
                <textarea
                  rows={3}
                  value={form.advocacy}
                  onChange={(e) => setForm((p) => ({ ...p, advocacy: e.target.value }))}
                  placeholder="e.g. Mental health awareness among youth"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border resize-none"
                  style={{ borderColor: formErrors.advocacy ? '#c0392b' : '#e2ddd6', color: '#000000' }}
                />
                {formErrors.advocacy && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>{formErrors.advocacy}</p>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleAddFinalist}
                disabled={isAdding}
                className="w-full py-3 rounded-full font-700 text-sm tracking-widest btn-gold shadow-gold disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isAdding ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Finalist
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}